<script setup lang="ts" generic="T extends NxPanelGridItem">
/**
 * Dashboard grid: panels in a grid of `columns` columns (12 by default), each
 * as wide as its `width` in columns. The array order is the screen order —
 * cells flow left to right and wrap into the next row. A custom view of
 * charts: the caller puts an `NxPanel` with a chart into each cell.
 *
 * The grid owns no data. `v-model:items` hands in `{ id, width }` entries
 * (extra fields of the caller's own type travel along) and gets a NEW array
 * back after every finished move or resize; the prop is never mutated.
 *
 * `editable` turns on the arrangement: the cells get an edit outline and a
 * resize grip on their lower right corner (`NxResizeHandles`). Moving goes
 * through a grip the caller renders in its panel — the `item` slot gets a
 * `handle` props object to bind onto it, e.g.
 * `<NxIconButton v-bind="handle" aria-label="Verschieben">`. The grid owns
 * that grip's pointer and keyboard behaviour:
 *
 * - Pointer: dragging the grip carries the cell along, a line marks where it
 *   lands (from the pointer's position over the cells); release drops it,
 *   Escape cancels. Dragging the corner snaps the width to whole columns of
 *   the measured grid, clamped to `minWidth`..`columns`; Escape cancels.
 * - Keyboard on the grip: ←/↑ one place earlier, →/↓ one place later,
 *   Shift + ←/→ one column narrower / wider. The corner grip takes ←/→ for
 *   one column as well. Every change is spoken through the kit's shared live
 *   region (`nxAnnounce`).
 *
 * Narrow containers (below `stackBelow` px) stack the cells — each takes the
 * full row — and resizing is off; moving still works.
 */
import { computed, nextTick, onBeforeUnmount, ref, useId, useTemplateRef, watch } from 'vue';
import { nxAnnounce } from '../Announce/announcer';
import { useElementSize } from '../Composables/observers';
import NxResizeHandles, {
  type NxResizeDelta,
  type NxResizeDirection,
} from '../ResizeHandles/NxResizeHandles.vue';
import { sortableDropSide, type NxSortableDropSide } from '../Sortable/sortMath';
import {
  clampPanelWidth,
  movePanelGridItem,
  panelGridPitch,
  panelGridTargetIndex,
  panelWidthFromDelta,
  resizePanelGridItem,
  type NxPanelGridItem,
  type NxPanelGridRect,
} from './panelGrid';

const props = withDefaults(
  defineProps<{
    /** Columns of the grid. */
    columns?: number;
    /** Narrowest a cell can be resized to, in columns. */
    minWidth?: number;
    /** Cells can be moved and resized; they show an edit outline. */
    editable?: boolean;
    /** Accessible name of the whole grid. */
    ariaLabel?: string;
    /** Name of a cell in the announcements and the corner grip. Defaults to its `id`. */
    itemLabel?: (item: T) => string;
    /** Container width in px below which every cell takes the full row and resizing is off. */
    stackBelow?: number;
    /** Spoken hint on the move grip (its `aria-describedby`). */
    keyboardHint?: string;
    /** Accessible name of a cell's corner grip. */
    resizeLabel?: (item: string) => string;
    /** Announcement after a move; `position` is 1-based. */
    movedLabel?: (item: string, position: number, count: number) => string;
    /** Announcement after a resize. */
    resizedLabel?: (item: string, width: number, columns: number) => string;
    /** Announcement when Escape cancelled a drag. */
    cancelledLabel?: (item: string) => string;
  }>(),
  {
    columns: 12,
    minWidth: 2,
    editable: false,
    ariaLabel: undefined,
    itemLabel: undefined,
    stackBelow: 900,
    keyboardHint: 'Pfeiltasten verschieben, Umschalt + Pfeil links oder rechts ändert die Breite',
    resizeLabel: (item: string) => `Breite von „${item}“ ändern`,
    movedLabel: (item: string, position: number, count: number) =>
      `„${item}“ auf Position ${position} von ${count} verschoben`,
    resizedLabel: (item: string, width: number, columns: number) =>
      `„${item}“ ist jetzt ${width} von ${columns} Spalten breit`,
    cancelledLabel: (item: string) => `Änderung an „${item}“ abgebrochen`,
  }
);

/** The cells in screen order. Updated with a new array after a move or resize. */
const items = defineModel<readonly T[]>('items', { required: true });

defineSlots<{
  /** A cell's content. `handle` goes onto the caller's move grip (`v-bind="handle"`); empty unless `editable`. */
  item?(props: { item: T; index: number; handle: Record<string, unknown> }): unknown;
  /** Same as `item`, for callers that use the default slot. */
  default?(props: { item: T; index: number; handle: Record<string, unknown> }): unknown;
}>();

const CORNER: readonly NxResizeDirection[] = ['se'];
/** Pointer travel (px) before a press on the grip becomes a drag. */
const DRAG_THRESHOLD = 4;

const hintId = useId();
const rootRef = useTemplateRef<HTMLElement>('root');
const { width: gridWidth } = useElementSize(rootRef);

/** Unmeasured (SSR, no ResizeObserver) counts as wide. */
const narrow = computed(() => gridWidth.value > 0 && gridWidth.value < props.stackBelow);
const canResize = computed(() => props.editable && !narrow.value);

function labelOf(item: T): string {
  return props.itemLabel ? props.itemLabel(item) : item.id;
}

function widthOf(item: T): number {
  return clampPanelWidth(item.width, props.minWidth, props.columns);
}

// ── Measuring ──────────────────────────────────────────────────────────────
const gap = ref(0);

function readGap(): number {
  const root = rootRef.value;
  if (!root || typeof getComputedStyle !== 'function') return 0;
  return parseFloat(getComputedStyle(root).columnGap) || 0;
}

watch(gridWidth, () => (gap.value = readGap()));

/** One column plus one gap, from the grid as it is now. 0 while unmeasured. */
function measurePitch(): number {
  const root = rootRef.value;
  if (!root) return 0;
  return panelGridPitch(root.getBoundingClientRect().width, props.columns, readGap());
}

/** The corner grip's arrow-key step: one column (1 px while unmeasured — still one column). */
const keyStep = computed(() => panelGridPitch(gridWidth.value, props.columns, gap.value) || 1);

function cells(): HTMLElement[] {
  return [...(rootRef.value?.querySelectorAll<HTMLElement>('[data-nx-panel-grid-cell]') ?? [])];
}

function focusHandle(id: string): void {
  // Compared by attribute rather than a selector: ids are arbitrary strings.
  for (const el of rootRef.value?.querySelectorAll<HTMLElement>('[data-nx-panel-grid-handle]') ??
    []) {
    if (el.dataset.nxPanelGridHandle === id) {
      el.focus();
      return;
    }
  }
}

// ── Commit ─────────────────────────────────────────────────────────────────
function moveTo(from: number, to: number, refocus: boolean): void {
  const item = items.value[from];
  if (!item) return;
  const next = movePanelGridItem(items.value, from, to);
  const index = next.indexOf(item);
  if (index === from) return;
  items.value = next;
  nxAnnounce(props.movedLabel(labelOf(item), index + 1, next.length));
  // The caller hands the new array back; Vue moves the cell, which can drop focus.
  if (refocus) void nextTick(() => focusHandle(item.id));
}

function resizeTo(index: number, width: number): void {
  const item = items.value[index];
  if (!item) return;
  const next = resizePanelGridItem(items.value, index, width, props.minWidth, props.columns);
  if (!next) return;
  items.value = next;
  nxAnnounce(props.resizedLabel(labelOf(item), next[index]!.width, props.columns));
}

// ── Pointer move ───────────────────────────────────────────────────────────
type Drag = {
  id: string;
  from: number;
  to: number;
  pointerId: number | undefined;
  startX: number;
  startY: number;
  dx: number;
  dy: number;
  started: boolean;
};
const drag = ref<Drag | null>(null);
const dragging = computed(() => (drag.value?.started ? drag.value : null));

function rects(current: Drag): NxPanelGridRect[] {
  return cells().map((el, index) => {
    const r = el.getBoundingClientRect();
    // The dragged cell follows the pointer; measure it where it stood.
    const sx = index === current.from ? current.dx : 0;
    const sy = index === current.from ? current.dy : 0;
    return { left: r.left - sx, right: r.right - sx, top: r.top - sy, bottom: r.bottom - sy };
  });
}

function samePointer(event: PointerEvent): boolean {
  const current = drag.value;
  return (
    !!current &&
    (current.pointerId === undefined ||
      event.pointerId === undefined ||
      event.pointerId === current.pointerId)
  );
}

function swallowClick(event: MouseEvent): void {
  // The release after a drag must not also click the grip.
  event.stopPropagation();
  event.preventDefault();
}

function onPointerDown(event: PointerEvent, index: number, id: string): void {
  if (!props.editable || resizing.value || (event.button !== undefined && event.button !== 0)) {
    return;
  }
  endDrag();
  const el = event.currentTarget as HTMLElement | null;
  if (event.pointerId !== undefined) {
    try {
      el?.setPointerCapture?.(event.pointerId);
    } catch {
      // No active pointer of that id (synthetic events): the moves still reach the grip.
    }
  }
  drag.value = {
    id,
    from: index,
    to: index,
    pointerId: event.pointerId,
    startX: event.clientX,
    startY: event.clientY,
    dx: 0,
    dy: 0,
    started: false,
  };
  window.addEventListener('keydown', onEscape, true);
}

function onPointerMove(event: PointerEvent): void {
  const current = drag.value;
  if (!current || !samePointer(event)) return;
  const dx = event.clientX - current.startX;
  const dy = event.clientY - current.startY;
  if (!current.started) {
    if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    current.started = true;
    window.getSelection?.()?.removeAllRanges();
  }
  event.preventDefault();
  current.dx = dx;
  current.dy = dy;
  current.to = panelGridTargetIndex(rects(current), current.from, event.clientX, event.clientY);
}

function onPointerUp(event: PointerEvent): void {
  const current = drag.value;
  if (!current || !samePointer(event)) return;
  endDrag();
  if (!current.started) return;
  window.addEventListener('click', swallowClick, { capture: true, once: true });
  // A click that never comes (released off the grip) must not eat the next one.
  setTimeout(() => window.removeEventListener('click', swallowClick, { capture: true }), 0);
  if (current.to !== current.from) moveTo(current.from, current.to, false);
}

function onPointerCancel(event: PointerEvent): void {
  if (samePointer(event)) endDrag();
}

function endDrag(): void {
  drag.value = null;
  window.removeEventListener('keydown', onEscape, true);
}

/** Escape during a pointer drag or a corner resize: put everything back. */
function onEscape(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  const moving = drag.value;
  const sizing = resizing.value;
  if (moving?.started) {
    endDrag();
    const item = items.value[moving.from];
    if (item) nxAnnounce(props.cancelledLabel(labelOf(item)));
  } else if (sizing && !sizing.cancelled) {
    sizing.cancelled = true;
    sizing.width = sizing.startWidth;
    const item = items.value[sizing.index];
    if (item) nxAnnounce(props.cancelledLabel(labelOf(item)));
  } else {
    return;
  }
  event.preventDefault();
  // Keep a surrounding dialog open: this Escape belonged to the drag.
  event.stopPropagation();
}

// ── Keyboard on the move grip ──────────────────────────────────────────────
function onHandleKeydown(event: KeyboardEvent, index: number): void {
  if (!props.editable || drag.value?.started) return;
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const { key } = event;
  if (event.shiftKey) {
    if (key !== 'ArrowLeft' && key !== 'ArrowRight') return;
    event.preventDefault();
    const item = items.value[index];
    if (!canResize.value || !item) return;
    resizeTo(index, widthOf(item) + (key === 'ArrowLeft' ? -1 : 1));
    return;
  }
  const step =
    key === 'ArrowLeft' || key === 'ArrowUp'
      ? -1
      : key === 'ArrowRight' || key === 'ArrowDown'
        ? 1
        : 0;
  if (step === 0) return;
  event.preventDefault();
  moveTo(index, index + step, true);
}

function handleProps(item: T, index: number): Record<string, unknown> {
  if (!props.editable) return {};
  return {
    'data-nx-panel-grid-handle': item.id,
    'aria-roledescription': 'Ziehgriff',
    'aria-describedby': hintId,
    'aria-keyshortcuts': 'ArrowLeft ArrowRight ArrowUp ArrowDown Shift+ArrowLeft Shift+ArrowRight',
    // Touch must not pan the page while the grip is held, and a drag must not
    // select the text it passes over.
    style: { touchAction: 'none', userSelect: 'none', cursor: 'grab' },
    onPointerdown: (event: PointerEvent) => onPointerDown(event, index, item.id),
    onPointermove: onPointerMove,
    onPointerup: onPointerUp,
    onPointercancel: onPointerCancel,
    // A link or image under the pointer would start the browser's own drag.
    onDragstart: (event: DragEvent) => event.preventDefault(),
    onKeydown: (event: KeyboardEvent) => onHandleKeydown(event, index),
  };
}

function dropSide(index: number): NxSortableDropSide | null {
  const current = dragging.value;
  if (!current || current.to !== index) return null;
  return sortableDropSide(current.from, current.to);
}

// ── Corner resize ──────────────────────────────────────────────────────────
type Resize = {
  index: number;
  startWidth: number;
  width: number;
  pitch: number;
  cancelled: boolean;
};
const resizing = ref<Resize | null>(null);

function onResizeStart(index: number): void {
  const item = items.value[index];
  if (!item || !canResize.value || drag.value) return;
  const width = widthOf(item);
  resizing.value = { index, startWidth: width, width, pitch: measurePitch(), cancelled: false };
  window.addEventListener('keydown', onEscape, true);
}

function onResize(delta: NxResizeDelta): void {
  const current = resizing.value;
  if (!current || current.cancelled) return;
  current.width = panelWidthFromDelta(
    current.startWidth,
    delta.dx,
    current.pitch,
    props.minWidth,
    props.columns
  );
}

function onResizeEnd(delta: NxResizeDelta): void {
  const current = resizing.value;
  resizing.value = null;
  window.removeEventListener('keydown', onEscape, true);
  if (!current || current.cancelled) return;
  resizeTo(
    current.index,
    panelWidthFromDelta(current.startWidth, delta.dx, current.pitch, props.minWidth, props.columns)
  );
}

// ── Rendering ──────────────────────────────────────────────────────────────
function cellStyle(item: T, index: number): Record<string, string | number> {
  const live = resizing.value?.index === index ? resizing.value.width : widthOf(item);
  const style: Record<string, string | number> = { '--panel-grid-span': live };
  const current = dragging.value;
  if (current?.from === index) {
    style.transform = `translate3d(${current.dx}px, ${current.dy}px, 0)`;
  }
  return style;
}

onBeforeUnmount(() => {
  endDrag();
  resizing.value = null;
  window.removeEventListener('keydown', onEscape, true);
});
</script>

<template>
  <div
    ref="root"
    class="nx-panel-grid"
    :class="{ 'is-editable': editable, 'is-narrow': narrow, 'is-dragging': !!dragging }"
    role="list"
    :aria-label="ariaLabel"
    :style="{ '--panel-grid-columns': columns }"
  >
    <div
      v-for="(item, index) in items"
      :key="item.id"
      class="nx-panel-grid__cell"
      :class="{
        'is-dragged': dragging?.from === index,
        'is-resizing': resizing?.index === index,
        'is-drop-before': dropSide(index) === 'before',
        'is-drop-after': dropSide(index) === 'after',
      }"
      role="listitem"
      :style="cellStyle(item, index)"
      :data-nx-panel-grid-cell="item.id"
    >
      <slot name="item" :item="item" :index="index" :handle="handleProps(item, index)">
        <slot :item="item" :index="index" :handle="handleProps(item, index)" />
      </slot>
      <NxResizeHandles
        v-if="canResize"
        :directions="CORNER"
        :step="keyStep"
        :big-step="keyStep"
        :label="resizeLabel(labelOf(item))"
        @resize-start="onResizeStart(index)"
        @resize="onResize"
        @resize-end="onResizeEnd"
      />
    </div>
    <span v-if="editable" :id="hintId" hidden>{{ keyboardHint }}</span>
  </div>
</template>

<style scoped>
.nx-panel-grid {
  display: grid;
  grid-template-columns: repeat(var(--panel-grid-columns), minmax(0, 1fr));
  gap: var(--nxt-space-md);
}

.nx-panel-grid__cell {
  position: relative;
  min-width: 0;
  grid-column: span var(--panel-grid-span);
  border-radius: var(--nxt-radius-md);
}
.nx-panel-grid.is-narrow > .nx-panel-grid__cell {
  grid-column: 1 / -1;
}

.nx-panel-grid.is-editable > .nx-panel-grid__cell {
  outline: var(--nxt-hairline) dashed var(--nxt-border-strong);
  outline-offset: var(--nxt-space-2xs);
}
.nx-panel-grid.is-editable > .nx-panel-grid__cell.is-resizing {
  outline-style: solid;
  outline-color: var(--nxt-brand-bg);
}
.nx-panel-grid.is-dragging {
  cursor: grabbing;
  user-select: none;
}
.nx-panel-grid__cell.is-dragged {
  z-index: var(--nxt-z-raised);
  opacity: 0.85;
  box-shadow: var(--nxt-shadow-md);
}

/* Insertion line in the gap the cell would land in: beside the target cell,
   above or below it when the cells are stacked. */
.nx-panel-grid__cell.is-drop-before::before,
.nx-panel-grid__cell.is-drop-after::after {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  width: calc(var(--nxt-hairline) * 3);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  pointer-events: none;
}
.nx-panel-grid__cell.is-drop-before::before {
  left: calc(var(--nxt-space-md) / -2 - var(--nxt-hairline) * 1.5);
}
.nx-panel-grid__cell.is-drop-after::after {
  right: calc(var(--nxt-space-md) / -2 - var(--nxt-hairline) * 1.5);
}
.nx-panel-grid.is-narrow > .nx-panel-grid__cell.is-drop-before::before,
.nx-panel-grid.is-narrow > .nx-panel-grid__cell.is-drop-after::after {
  left: 0;
  right: 0;
  width: auto;
  height: calc(var(--nxt-hairline) * 3);
}
.nx-panel-grid.is-narrow > .nx-panel-grid__cell.is-drop-before::before {
  top: calc(var(--nxt-space-md) / -2 - var(--nxt-hairline) * 1.5);
  bottom: auto;
}
.nx-panel-grid.is-narrow > .nx-panel-grid__cell.is-drop-after::after {
  top: auto;
  bottom: calc(var(--nxt-space-md) / -2 - var(--nxt-hairline) * 1.5);
}

@media (forced-colors: active) {
  .nx-panel-grid__cell.is-drop-before::before,
  .nx-panel-grid__cell.is-drop-after::after {
    background: Highlight;
  }
}
</style>
